import type { EndEvent, RTCSession } from 'jssip/lib/RTCSession';

import { LogLevel } from 'features/analytics/enums';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { sendAnalytics } from 'features/analytics/store';
import { RtcSessionTracker } from 'features/sip/services';
import { getConnectionChangeTypeByOriginator } from 'features/sip/utils';

// Define the dispatch type based on the createAsyncThunk dispatch
type AsyncThunkDispatch = (action: any) => any;

/**
 * Analytics helper for session tracking events
 */
export const sessionTrackingAnalytics = {
  /**
   * Log when a regular session is accepted and tracked as original
   */
  originalSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.originalSessionAccepted',
        Level: LogLevel.INFO,
        Message: 'Original session accepted and tracked for potential REFER',
        SessionRemoteUri: sessionRemoteUri,
        SessionDirection: session.direction,
        SessionId: session.id,
      })
    );
  },

  /**
   * Log when a teaming session is accepted
   */
  teamingSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession,
    originalSessionUri?: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.teamingSessionAccepted',
        Level: LogLevel.INFO,
        Message:
          'Teaming session accepted, configured refer to use original session',
        TeamingSessionUri: sessionRemoteUri,
        TeamingSessionDirection: session.direction,
        TeamingSessionId: session.id,
        OriginalSessionUri: originalSessionUri,
      })
    );
  },

  /**
   * Log when session references are updated
   */
  sessionReferencesUpdated: (
    dispatch: AsyncThunkDispatch,
    operation: string,
    details: Record<string, any>
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.sessionReferencesUpdated',
        Level: LogLevel.INFO,
        Message: `Session references updated: ${operation}`,
        ...details,
      })
    );
  },

  /**
   * Log when all session references are cleaned up
   */
  allSessionsCleanedUp: (dispatch: AsyncThunkDispatch, reason: string) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.allSessionsCleanedUp',
        Level: LogLevel.INFO,
        Message: 'All session references cleared',
        Reason: reason,
      })
    );
  },

  /**
   * Log when a session is terminated
   */
  sessionTerminated: (
    dispatch: AsyncThunkDispatch,
    sessionId: string | undefined,
    reason: string,
    terminateOptions?: any
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.sessionTerminated',
        Level: LogLevel.INFO,
        Message: 'Session terminated',
        SessionId: sessionId,
        Reason: reason,
        TerminateOptions: terminateOptions,
      })
    );
  },

  /**
   * Log when refer session ref is updated
   */
  referSessionUpdated: (
    dispatch: AsyncThunkDispatch,
    newSessionUri: string | null,
    reason: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.referSessionUpdated',
        Level: LogLevel.INFO,
        Message: 'Refer session reference updated',
        NewReferSessionUri: newSessionUri,
        Reason: reason,
      })
    );
  },
};

/**
 * Analytics helper for session end events
 */
export const sessionEndAnalytics = {
  /**
   * Log initial session end details
   */
  sessionEndInitiated: (
    dispatch: AsyncThunkDispatch,
    params: {
      event: EndEvent;
      isRefer: boolean;
      isTeaming: boolean;
    }
  ) => {
    const { event, isRefer, isTeaming } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.sessionEndInitiated',
        Level: LogLevel.INFO,
        Message: 'Processing session end event',
        Originator: event.originator,
        Cause: event.cause,
        IsRefer: isRefer,
        IsTeaming: isTeaming,
        EventDebug: {
          messageExists: !!event.message,
          fromExists: !!event.message?.from,
          toExists: !!event.message?.to,
          messageString: event.message?.toString(),
        },
      })
    );
  },

  /**
   * Log when ignoring BYE messages during teaming
   */
  ignoringByeMessage: (dispatch: AsyncThunkDispatch, isTeaming: boolean) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk',
        Message:
          'Ignoring BYE message not from svrs-rt endpoint during teaming',
        Level: LogLevel.INFO,
        IsInFreeSwitchRoom: isTeaming,
      })
    );
  },

  /**
   * Log basic session end details
   */
  sessionEnded: (dispatch: AsyncThunkDispatch, event: EndEvent) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEnded',
        Originator: event.originator,
        Cause: event.cause,
        Message: event.message?.toString() ?? '',
        Level: LogLevel.INFO,
      })
    );
  },

  /**
   * Log when completing teaming
   */
  completingTeaming: (dispatch: AsyncThunkDispatch, event: EndEvent) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.completingTeaming',
        Level: LogLevel.INFO,
        Message: 'Completing teaming operation due to session end',
        Cause: event.cause,
        Originator: event.originator,
      })
    );
  },

  /**
   * Log when skipping teaming completion
   */
  skipTeamingCompletion: (
    dispatch: AsyncThunkDispatch,
    event: EndEvent,
    reason: string
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipTeamingCompletion',
        Level: LogLevel.INFO,
        Message: 'Skipping teaming completion',
        Reason: reason,
        Cause: event.cause,
      })
    );
  },

  /**
   * Log when resetting remote video
   */
  resetRemoteVideo: (
    dispatch: AsyncThunkDispatch,
    isRefer: boolean,
    isTeaming: boolean
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.resetRemoteVideo',
        Level: LogLevel.INFO,
        Message: 'Resetting remote video due to session end',
        IsRefer: isRefer,
        IsTeaming: isTeaming,
      })
    );
  },

  /**
   * Log when skipping remote video reset
   */
  skipRemoteVideoReset: (dispatch: AsyncThunkDispatch) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipRemoteVideoReset',
        Level: LogLevel.INFO,
        Message: 'Skipping remote video reset for REFER during teaming',
      })
    );
  },

  /**
   * Log emitting deaf disconnect event
   */
  emitDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    event: EndEvent,
    isSpecialEndEvent: boolean
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.emitDeafDisconnect',
        Level: LogLevel.INFO,
        Message: 'Emitting deaf disconnect event',
        ConnectionChangeType: getConnectionChangeTypeByOriginator(
          event.originator
        ),
        IsConnectionCallStateNeeded: !isSpecialEndEvent,
      })
    );
  },

  /**
   * Log skipping deaf disconnect
   */
  skipDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    params: {
      isOriginalReferSession: boolean;
      isRefer: boolean;
      isTeaming: boolean;
      shouldSkipDeafDisconnect: boolean;
    }
  ) => {
    const {
      isOriginalReferSession,
      isRefer,
      isTeaming,
      shouldSkipDeafDisconnect,
    } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipDeafDisconnect',
        Level: LogLevel.INFO,
        Message:
          'Skipping deaf disconnect for original REFER session during teaming',
        IsOriginalReferSession: isOriginalReferSession,
        IsRefer: isRefer,
        IsTeaming: isTeaming,
        ShouldSkipDeafDisconnect: shouldSkipDeafDisconnect,
      })
    );
  },

  /**
   * Log clearing direct session
   */
  clearDirectSession: (dispatch: AsyncThunkDispatch, sessionId?: string) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.clearDirectSession',
        Level: LogLevel.INFO,
        Message: 'Clearing direct session tracking',
        SessionId: sessionId,
      })
    );
  },

  /**
   * Log performing cleanup actions
   */
  performCleanupActions: (dispatch: AsyncThunkDispatch) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.performCleanupActions',
        Level: LogLevel.INFO,
        Message: 'Performing cleanup actions',
        Actions: ['forceCancelColdHandoff', 'leaveVriChat'],
      })
    );
  },

  /**
   * Log skipping cleanup actions
   */
  skipCleanupActions: (dispatch: AsyncThunkDispatch, reason: string) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipCleanupActions',
        Level: LogLevel.INFO,
        Message: 'Skipping cleanup actions',
        Reason: reason,
      })
    );
  },

  /**
   * Log session end processing completion
   */
  completed: (
    dispatch: AsyncThunkDispatch,
    params: {
      event: EndEvent;
      isRefer: boolean;
      isOriginalReferSession: boolean;
      isLeaveTeaming: boolean;
      isLeaveHandoff: boolean;
      isTeaming: boolean;
    }
  ) => {
    const {
      event,
      isRefer,
      isOriginalReferSession,
      isLeaveTeaming,
      isLeaveHandoff,
      isTeaming,
    } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.completed',
        Level: LogLevel.INFO,
        Message: 'Session end processing completed',
        ProcessedCause: event.cause,
        ProcessedOriginator: event.originator,
        FinalState: {
          clearedDirectSession: isRefer && isOriginalReferSession,
          completedTeaming: !isLeaveTeaming && !isRefer,
          resetRemoteVideo: !(isRefer && isTeaming),
          performedCleanup: !isLeaveHandoff && !isRefer,
        },
      })
    );
  },
};

/**
 * Centralized analytics for session accepted events (teaming/original)
 */
export function trackSessionAcceptedAnalytics(
  dispatch: AsyncThunkDispatch,
  sessionRole: string | undefined,
  currentSession: RTCSession
) {
  const sessionRemoteUri =
    currentSession.remote_identity?.uri?.toString() || '';

  if (sessionRole === 'teaming') {
    sessionTrackingAnalytics.teamingSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession,
      RtcSessionTracker.getDirectSession()?.remote_identity?.uri?.toString()
    );
  } else if (sessionRole === 'direct') {
    sessionTrackingAnalytics.originalSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession
    );
  } else {
    // Fallback: unknown session role
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.unknownSessionRole',
        Level: LogLevel.WARNING,
        Message: 'Session accepted with unknown role',
        SessionRemoteUri: sessionRemoteUri,
        SessionId: currentSession.id,
      })
    );
  }
}
